import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { EditableFieldNode } from '../../../models/ui/editable-field-node.model';
import { flatMapDeep } from 'lodash-es';
import { EditableField, EditableFieldSection } from '../../../models/ui/editable-field-section.model';
import { Observable } from 'rxjs';
import { BaseComponent } from '../../base.component';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';

@Component({
    selector: 'lex-field-editor',
    templateUrl: './field-editor.component.html',
    styleUrls: ['./field-editor.component.scss'],
    standalone: false
})
export class FieldEditorComponent extends BaseComponent implements OnInit {
	public fields: EditableFieldSection[];
	public fieldFormGroup: UntypedFormGroup = new UntypedFormGroup({});

	@Input()
	public fields$: Observable<EditableFieldSection[]>;
	@Input()
	public config: FieldEditorConfiguration;
	@Output()
	public itemChanged = new EventEmitter<EditableFieldNode | EditableFieldSection>();
	@Output()
	public itemDeleted = new EventEmitter<EditableFieldNode | EditableFieldSection>();
	@Output()
	public refreshItems = new EventEmitter<void>();
	@Output()
	public addItem = new EventEmitter<void>();
	@Output()
	public importFile = new EventEmitter<File>();

	@ViewChild('fileUpload', { static: false }) fileInputElement: ElementRef;

	constructor(private dialog: MatDialog) {
		super();
	}

	ngOnInit(): void {
		this.fields$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
			response.forEach((item) => {
				const section = this.fields?.find((field) => field.id === item.id);
				item.expanded = section?.expanded;
			});
			this.fields = response;
			this.createForm();
		});
	}

	public dropSection(event: CdkDragDrop<EditableFieldSection, EditableFieldSection, EditableFieldSection>): void {
		if (!(event.item.data instanceof EditableFieldSection)) {
			return;
		}
		moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
		this.saveChanges(event.item.data);
	}

	public dropNode(event: CdkDragDrop<EditableFieldNode, EditableFieldNode, EditableFieldNode>) {
		const isItemANode = event.item.data instanceof EditableFieldNode;
		const isItemInTheSameContainer = event.previousContainer?.data?.id === event.container?.data?.id;
		const isItemInTheSameIndex = event.previousIndex === event.currentIndex;
		const isItemMoved = !(isItemInTheSameContainer && isItemInTheSameIndex);
		const shouldMoveNode = !isItemANode || isItemMoved;
		if (!shouldMoveNode) {
			return;
		}

		const currentContainer = this.fields.find((parent) => {
			return parent.id === event.container.data.id;
		})?.items;

		if (isItemInTheSameContainer) {
			moveItemInArray(currentContainer, event.previousIndex, event.currentIndex);
		} else {
			const previousContainer = this.fields.find((parent) => {
				return parent.id === event.previousContainer?.data?.id;
			})?.items;
			event.item.data.parentId = event.container.data.id;
			transferArrayItem(previousContainer, currentContainer, event.previousIndex, event.currentIndex);
		}
		this.saveChanges(event.item.data);
	}

	public add(): void {
		this.addItem.emit();
	}

	public import(event) {
		const file: File = event.target.files[0];
		this.importFile.emit(file);
	}

	public toggleExpandCollapse(): void {
		const isEveryFieldExpanded = this.fields.every((field) => field.expanded);
		this.fields.forEach((field) => {
			field.expanded = !isEveryFieldExpanded;
		});
	}

	public delete($event: any, node: EditableFieldNode | EditableFieldSection): void {
		$event.stopPropagation();
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Editable Field`,
			message: `This field will be <strong>PERMANENTLY DELETED</strong>.
<br /> Every document coded for this custom field will lose that data.
<br /> This field will be removed from any documents and will not be available to add or edit on any documents.
<br /> <strong>This action cannot be undone.</strong>`,
			showAcknowledge: true
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.subscribe((okClicked) => {
				if (okClicked) {
					this.itemDeleted.emit(node);
				}
			});
	}

	public startEdit($event: any, node: EditableFieldNode | EditableFieldSection): void {
		$event.stopPropagation();
		node.isEditing = true;
	}

	public stopEdit($event: any, node: EditableFieldNode | EditableFieldSection): void {
		$event.stopPropagation();
		const flattenedCollection = flatMapDeep(this.fields, (item) => [item, ...item.items]);

		if (flattenedCollection.some((x) => x.label === node.label && x.id !== node.id)) {
			return;
		}

		node.isEditing = false;
		this.saveChanges(node);
	}

	public cancelEdit($event: any, node?: EditableFieldNode | EditableFieldSection): void {
		$event.stopPropagation();

		if (!node) {
			node.isEditing = false;
		}
		this.refreshItems.emit();
	}

	public createFormControl(field: EditableField): UntypedFormControl {
		const control = new UntypedFormControl(field.label, [Validators.required, this.duplicateTitleValidatorFn()]);

		control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			field.label = value;
		});

		return control;
	}

	public createForm(): void {
		const flattenedCollection = flatMapDeep(this.fields, (item) => [item, ...item.items]);
		const formControls = {};

		flattenedCollection.forEach((field) => {
			formControls[`field-${field.id}`] = this.createFormControl(field);
		});

		this.fieldFormGroup =  new UntypedFormGroup(formControls);
	}

	public duplicateTitleValidatorFn(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const forbidden = this.isDuplicateTitle(control.value);
			return forbidden ? { 'duplicateTitle': { value: control.value } } : null;
		};
	}

	public isDuplicateTitle(title: string): boolean {
		const flattenedCollection = flatMapDeep(this.fields, (item) => [item, ...item.items]);
		const allTitles = flattenedCollection.map(cf => cf.label.toLowerCase());
		return allTitles.includes(title?.toLowerCase());
	}

	private saveChanges(node: EditableFieldNode | EditableFieldSection): void {
		const flattenedCollection = flatMapDeep(this.fields, (item) => [item, ...item.items]);

		flattenedCollection.forEach((item, index) => {
			item.rank = index + 1;
		});

		const changedItem = flattenedCollection.find((item) => item.id === node.id);
		this.itemChanged.emit(changedItem);
	}
}

interface FieldEditorConfiguration {
	disableMenu?: boolean;
	title?: string;
}
